import React from "react";
import { DndProvider, useDragDropManager } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";
import parse from "html-react-parser";

import { ArrayLayoutProps, composePaths, ControlElement, createDefaultValue } from "@jsonforms/core";

import plus from "@ingka/ssr-icon/paths/plus";
import Button from "@ingka/button";
import Toast from "@ingka/toast";

import CardRenderer from "./CardRenderer";
import { CenterButtonWrapper, SrOnly } from "../../styles";
import LayoutHeading from "../LayoutHeading";
import { findAndTransformHTMLString } from "../../renderers/utils";
import VisuallyHidden from "../../VisuallyHidden/VisuallyHidden";

interface CustomArrayLayoutProps extends ArrayLayoutProps {
	addItemCb: (p: string, value: any) => () => void;
	removeItemsCb: (path: string, toDelete: number[]) => () => void;
	onMoveUp: (path: string, toMove: number) => (() => void) | undefined;
	onMoveDown: (path: string, toMove: number) => (() => void) | undefined;
	onMoveToTop: (currentIndex: number) => () => void;
	onMoveToBottom: (currentIndex: number) => () => void;
	onMoveCard: (dragIndex: number, hoverIndex: number) => void;
	announcement: string;
	setAnnouncement: React.Dispatch<React.SetStateAction<string>>;
	elements: any;
	focusIndex: string;
	setfocusIndex: React.Dispatch<React.SetStateAction<string>>;
	accordionTitleHeadingLevel: number;
	setDragFocusIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
	isDraggingSelected: boolean;
	setIsDraggingSelected: React.Dispatch<React.SetStateAction<boolean>>;
	isChildArray: boolean;
	isEmpty: boolean;
	uischema: {
		subText?: string;
		depth?: number;
		headingLevel?: number;
	} & ControlElement;
	onCopyArrayValues: (index: number) => void;
	onPasteArrayValues: (index: number) => void;
	canPaste: boolean;
	toastMessage: string;
	setToastMessage: React.Dispatch<React.SetStateAction<string>>;
	dragFocusIndex?: number;
	subText?: string;
	arrayLabel?: string;
	showAccrordion?: boolean;
	headingLevel?: number;
}

// Wrapper component that only renders DndProvider if one doesn't already exist
const DndProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	try {
		// This will throw if there's no DndProvider ancestor
		useDragDropManager();
		// If we get here, a DndProvider already exists, so just render children
		return <>{children}</>;
	} catch {
		// No DndProvider exists, so we need to create one
		return <DndProvider backend={HTML5Backend}>{children}</DndProvider>;
	}
};

const StyledDiv = styled.div`
	margin: 1.5rem 0;
	flex-grow: 1;
`;

const StyledParagraph = styled.p`
	margin-top: 0rem;
`;

const ParagraphWrapper = styled.div`
	text-align: center;
`;

const Layout: React.FC<CustomArrayLayoutProps> = ({
	uischema,
	schema,
	renderers,
	cells,
	data,
	path,
	addItemCb,
	removeItemsCb,
	onMoveUp,
	onMoveDown,
	onMoveToTop,
	onMoveToBottom,
	onMoveCard,
	enabled,
	showAccrordion = false,
	headingLevel,
	elements,
	arrayLabel,
	announcement,
	setAnnouncement,
	subText,
	focusIndex,
	accordionTitleHeadingLevel,
	setDragFocusIndex,
	dragFocusIndex,
	isDraggingSelected,
	setIsDraggingSelected,
	isChildArray,
	isEmpty,
	setfocusIndex,
	onCopyArrayValues,
	onPasteArrayValues,
	canPaste,
	toastMessage,
	setToastMessage,
}) => {
	const toRender = Array(data)
		.fill(0)
		.map((_, i) => {
			return (
				<CardRenderer
					key={i}
					index={i}
					schema={schema}
					uischema={uischema}
					path={composePaths(path, `${i}`)}
					renderers={renderers}
					cells={cells}
					onRemove={removeItemsCb(path, [i])}
					elements={elements}
					onMoveDown={onMoveDown(path, i)}
					onMoveUp={onMoveUp(path, i)}
					onMoveToTop={onMoveToTop(i)}
					onMoveToBottom={onMoveToBottom(i)}
					moveCard={onMoveCard}
					label={arrayLabel}
					data={data}
					removable={uischema.options?.removable}
					enabled={enabled}
					isFocused={composePaths(path, `${i}`) === composePaths(focusIndex, `${data - 1}`)}
					headingLevel={accordionTitleHeadingLevel}
					setDragFocusIndex={setDragFocusIndex}
					dragFocusIndex={dragFocusIndex}
					isDraggingSelected={isDraggingSelected}
					setIsDraggingSelected={setIsDraggingSelected}
					setAnnouncement={setAnnouncement}
					showAccrordion={showAccrordion}
					isChildArray={isChildArray}
					onCopyArrayValues={onCopyArrayValues}
					onPasteArrayValues={onPasteArrayValues}
					canPaste={canPaste}
				/>
			);
		});

	return (
		<StyledDiv>
			<SrOnly aria-live="assertive" aria-atomic="true">
				{announcement}
			</SrOnly>

			{arrayLabel && <LayoutHeading label={arrayLabel} headingLevel={headingLevel} />}

			{subText && <StyledParagraph>{parse(findAndTransformHTMLString(subText))}</StyledParagraph>}
			{isEmpty && (
				<ParagraphWrapper>
					<p>No data</p>
				</ParagraphWrapper>
			)}
			<DndProviderWrapper>
				<VisuallyHidden id="drag-instructions">
					Press space or enter to grab this item. Use arrow keys to change position, then space or enter to drop.
				</VisuallyHidden>
				<div role="list" aria-label="reorderable list">
					{toRender}
				</div>
			</DndProviderWrapper>
			<CenterButtonWrapper>
				<Button
					id={`add-${path}`}
					type="secondary"
					onClick={() => {
						setfocusIndex(path);
						addItemCb(path, createDefaultValue(schema, schema))();
					}}
					text="Add Item"
					ssrIcon={plus}
					iconPosition="trailing"
					size="small"
					disabled={!enabled || uischema.options?.addable === false}
					aria-label={`Add ${isEmpty ? "an item" : "another item"}${arrayLabel ? ` to ${arrayLabel}` : ""}`}
				/>
			</CenterButtonWrapper>
			<Toast
				text={toastMessage}
				isOpen={!!toastMessage}
				onCloseRequest={() => setToastMessage("")}
				ariaLabelCloseBtn="Dismiss notification"
			/>
		</StyledDiv>
	);
};

export default Layout;
